import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Calendar, 
  Zap, 
  Settings, 
  ChevronRight, 
  Clock, 
  Shield, 
  DollarSign,
  Star,
  Award,
  Users,
  Bell,
  X,
  Key,
  Eye,
  EyeOff,
  Copy
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import VerificationBanner from '../../components/VerificationBanner';
import LicenseModal from '../../components/LicenseModal';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';
import stripeService from '../../services/stripe';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isVerified = user?.IsEmailVerified === true || user?.isEmailVerified === true;

  // Helper function to determine subscription data from license status
  const determineSubscriptionFromLicense = (licenseStatus) => {
    if (licenseStatus.hasPaidLicense && licenseStatus.subscriptionType) {
      const license = licenseStatus.paidLicense;
      const expirationDate = new Date(license.expirationDate);
      
      // Parse subscription type from backend (e.g., "Professional - Monthly", "Professional - Annual")
      const subscriptionParts = licenseStatus.subscriptionType.split(' - ');
      const tier = subscriptionParts.length > 1 ? subscriptionParts[1] : 'Unknown';
      
      // Determine billing display based on tier
      let billing = 'Custom Plan';
      if (tier === 'Monthly') {
        billing = '$119.99/month';
      } else if (tier === 'Quarterly' || tier === '3 Months') {
        billing = '3-Month Plan';
      } else if (tier === 'Semiannual' || tier === '6 Months') {
        billing = '6-Month Plan';
      } else if (tier === 'Annual') {
        billing = 'Annual Plan';
      }
      
      return {
        plan: 'pro',
        status: licenseStatus.subscriptionStatus || 'active',
        nextBillingDate: expirationDate.toISOString(),
        paymentMethod: {
          type: 'credit_card',
          last4: '****', // We don't have this info from license
          expiryMonth: null,
          expiryYear: null
        },
        tier: tier,
        billing: billing,
        subscriptionType: licenseStatus.subscriptionType,
        features: [
          'Full professional features',
          'Priority support',
          'Advanced reporting',
          'Unlimited customers',
          'Export capabilities',
          'Custom workflows'
        ]
      };
    }
    
    // Default to free plan if no paid license
    return {
      plan: 'free',
      status: 'active',
      nextBillingDate: null,
      paymentMethod: null,
      tier: 'Free',
      billing: '$0.00 - Free Plan',
      subscriptionType: null,
      features: [
        'Basic shop management',
        'Customer database',
        'Basic reporting',
        'Community support'
      ]
    };
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [showActivationKey, setShowActivationKey] = useState(false);
  const [activationKey, setActivationKey] = useState('');
  const [keycopied, setKeyCopied] = useState(false);
  const [showTrialKey, setShowTrialKey] = useState(false);
  const [trialKey, setTrialKey] = useState('');
  const [trialKeyCopied, setTrialKeyCopied] = useState(false);
  const [, setLicenses] = useState({ trialLicense: null, paidLicenses: [] });
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [showNewLicenseModal, setShowNewLicenseModal] = useState(false);
  const [newPurchasedLicense, setNewPurchasedLicense] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState({
    hasPaidLicense: false,
    trialLicense: null,
    paidLicense: null,
    canPurchaseLicense: true
  });
  const [subscription, setSubscription] = useState({
    plan: 'free',
    status: 'active',
    nextBillingDate: null,
    paymentMethod: null,
    features: []
  });
  const [selectedTier, setSelectedTier] = useState('monthly');
  
  // Stripe price IDs mapping
  const stripePriceIds = {
    monthly: 'price_1ROmMCH024do87DzCJQz1Fmr',
    quarterly: 'price_1ROmMCH024do87DzKUfa1Jg0',
    semiannual: 'price_1ROmMCH024do87DzY2cnLn12',
    annual: 'price_1ROmMCH024do87DzoyEQaF8z'
  };

  // Mock subscription plans
  const plans = [
    {
      id: 'pro',
      name: 'Torque Professional',
      price: '$119.99',
      frequency: 'per month',
      color: 'bg-blue-100 dark:bg-blue-900',
      features: [
        'Single User License',
        'Unlimited Invoices',
        'Advanced Customer Management',
        'Complete Vehicle Database with FREE yearly updates',
        'Comprehensive Reports and Analytics',
        'Email Notifications and Reminders',
        'Easy PDF Invoice Generation'
      ],
      icon: Star,
      cta: subscription.plan === 'pro' ? 'Current Plan' : 'Choose Plan',
      disabled: subscription.plan === 'pro',
      current: subscription.plan === 'pro',
      description: 'Our flagship product provides a complete solution for your automotive service business.',
      priceOptions: [
        { duration: 'Monthly', price: '$119.99/mo' },
        { duration: '3 Months', price: '$109.99/mo', savings: 'Save $30' },
        { duration: '6 Months', price: '$99.99/mo', savings: 'Save $120' },
        { duration: 'Annual', price: '$999/year', savings: 'Save $440' }
      ]
    },
    {
      id: 'cloud',
      name: 'Torque Cloud',
      price: 'Coming Soon',
      frequency: '',
      color: 'bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-700 dark:to-indigo-800',
      features: [
        'All Professional features included',
        'Cloud invoice storage and data sync',
        'Real-time synchronization',
        'Cross-device compatibility',
        'Automatic cloud backups'
      ],
      icon: Award,
      cta: 'Notify Me',
      disabled: false,
      current: subscription.plan === 'cloud',
      isCloud: true,
      description: 'Access your business data anytime, anywhere with our cloud-connected version. Collaborate with your team in real-time and enjoy automatic backups and updates.'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        if (!user) {
          navigate('/login');
          return;
        }

        // Check for purchase success in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const isSuccessfulPurchase = urlParams.get('success') === 'true';
        
        if (isSuccessfulPurchase) {
          setPurchaseSuccess(true);
          // Remove the success param from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check for recently purchased license and show modal
          try {
            const recentLicenseResponse = await api.get('/License/my-licenses?recentOnly=true');
            if (recentLicenseResponse.data.recentPurchaseLicense) {
              setNewPurchasedLicense(recentLicenseResponse.data.recentPurchaseLicense);
              setShowNewLicenseModal(true);
            }
          } catch (error) {
            console.error('Failed to fetch recent license:', error);
          }
        }
        
        // Load pricing data from Stripe config
        try {
          await stripeService.getPricing();
        } catch (error) {
          console.error('Failed to fetch pricing:', error);
        }

        // Fetch user's license status (for UI display logic)
        try {
          const statusResponse = await api.get('/License/my-license-status');
          setLicenseStatus(statusResponse.data);
          
          // Set subscription data based on actual license status
          const subscriptionData = determineSubscriptionFromLicense(statusResponse.data);
          setSubscription(subscriptionData);
          
          // Set the license keys for display
          if (statusResponse.data.trialLicense) {
            setTrialKey(statusResponse.data.trialLicense.licenseKey);
          }
          if (statusResponse.data.paidLicense) {
            setActivationKey(statusResponse.data.paidLicense.licenseKey);
          }
        } catch (error) {
          console.error('Failed to fetch license status:', error);
          // Fallback to free plan if license status fetch fails
          const subscriptionData = determineSubscriptionFromLicense({ hasPaidLicense: false, paidLicense: null });
          setSubscription(subscriptionData);
        }

        // Fetch user's licenses (normal flow - all licenses)
        try {
          const licensesResponse = await api.get('/License/my-licenses');
          setLicenses(licensesResponse.data);
        } catch (error) {
          console.error('Failed to fetch licenses:', error);
        }
      } catch (err) {
        setError('Failed to load subscription data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleUpgrade = async (planId, isCloud) => {
    if (planId === 'free_trial') {
      navigate('/downloads');
      return;
    }
    
    if (isCloud) {
      setShowNotifyModal(true);
      return;
    }
    
    // Start Stripe checkout for Professional plan
    if (planId === 'pro') {
      // Check if user can purchase a license
      if (!licenseStatus.canPurchaseLicense) {
        alert('You already have a paid license. Please use the "Manage Subscription" button to view, cancel, or change your current license.');
        return;
      }
      
      try {
        const user = getCurrentUser();
        const priceId = stripePriceIds[selectedTier];
        
        console.log(`Creating checkout session with selectedTier: ${selectedTier}, price ID: ${priceId} for user: ${user.id}`);
        
        const session = await stripeService.createCheckoutSession(
          priceId,
          user.id,
          `${window.location.origin}/account/subscription?success=true`,
          `${window.location.origin}/account/subscription`
        );
        
        await stripeService.redirectToCheckout(session.sessionId);
      } catch (error) {
        console.error('Error creating checkout:', error);
        if (error.message && error.message.includes('already has a paid license')) {
          alert('You already have a paid license. Please use the "Manage Subscription" button to view, cancel, or change your current license.');
        } else {
          alert('Failed to start checkout. Please try again.');
        }
      }
    }
  };

  const getTierName = (tier) => {
    const names = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semi-Annual',
      annual: 'Annual'
    };
    return names[tier] || tier;
  };

  const openCustomerPortal = async () => {
    try {
      const user = getCurrentUser();
      console.log('Opening customer portal for user:', user.id);
      
      const portalData = await stripeService.createCustomerPortalSession(user.id.toString());
      console.log('Portal data received:', portalData);
      
      if (portalData.url || portalData) {
        // Handle both response formats: {url: "..."} or direct URL string
        const portalUrl = portalData.url || portalData;
        console.log('Redirecting to portal:', portalUrl);
        window.location.href = portalUrl;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Provide more specific error message
      let errorMessage = 'Failed to open customer portal. ';
      if (error.response?.status === 404) {
        errorMessage += 'Customer not found. Please ensure you have an active subscription.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else {
        errorMessage += 'Please try again or contact support.';
      }
      
      alert(errorMessage);
    }
  };

  const testCheckout = async () => {
    try {
      const user = getCurrentUser();
      const session = await stripeService.createCheckoutSession(
        'price_1ROmMCH024do87DzCJQz1Fmr', // Monthly price
        user.id,
        `${window.location.origin}/account/subscription?success=true`,
        `${window.location.origin}/account/subscription`
      );
      
      await stripeService.redirectToCheckout(session.sessionId);
    } catch (error) {
      console.error('Error creating test checkout:', error);
      alert('Failed to create checkout session. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const PlanIcon = ({ plan }) => {
    const Icon = plan.icon;
    return (
      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${plan.id === 'cloud' ? plan.color : plan.color}`}>
        <Icon className={`w-6 h-6 ${plan.id === 'cloud' ? 'text-white' : 'text-gray-800 dark:text-white'}`} />
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 color-transition">
      <Navbar />
      <VerificationBanner />

      <div className="flex-grow container mx-auto px-4 sm:px-5 lg:px-7 py-10 mt-16">
        {/* Account navigation breadcrumbs */}
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to="/account/subscription" className="text-blue-600 dark:text-blue-400 font-medium">Subscription</Link>
        </div>

        {/* Account navigation tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
          <Link 
            to="/account/profile" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Profile
          </Link>
          <Link 
            to="/account/subscription" 
            className="px-4 py-2 text-sm font-medium border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400"
            aria-current="page"
          >
            Subscription
          </Link>
          <Link 
            to="/account/settings" 
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Settings
          </Link>
          {/* <Link
            to="/account/referrals"
            className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
          >
            Referrals
          </Link> */}
        </div>

        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md mb-6 flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Purchase Success Banner */}
              {purchaseSuccess && (
                <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-6 rounded-lg border-2 border-green-300 dark:border-green-700 shadow-lg">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800/50 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-700 dark:text-green-300" />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-1">
                        Payment Successful!
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                        Your purchase has been completed successfully. Your new license key is now available below.
                      </p>
                      <button
                        onClick={() => setPurchaseSuccess(false)}
                        className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Current subscription overview */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition mb-8">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Current Subscription</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                        subscription.plan === 'free' 
                          ? 'bg-gray-100 dark:bg-gray-700' 
                          : subscription.plan === 'pro'
                            ? 'bg-blue-100 dark:bg-blue-900'
                            : 'bg-purple-100 dark:bg-purple-900'
                      }`}>
                        {subscription.plan === 'free' ? (
                          <Users className="w-6 h-6 text-gray-800 dark:text-white" />
                        ) : subscription.plan === 'pro' ? (
                          <Star className="w-6 h-6 text-blue-800 dark:text-blue-200" />
                        ) : (
                          <Award className="w-6 h-6 text-purple-800 dark:text-purple-200" />
                        )}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} Plan
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {subscription.plan === 'free' 
                            ? 'Basic access to Torque features' 
                            : subscription.plan === 'pro'
                              ? `Professional ${subscription.tier} Plan - Priority support included`
                              : 'Enterprise features with dedicated support'
                          }
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm flex items-center ${
                      subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                        : subscription.status === 'canceled'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                      {subscription.status === 'active' ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Active
                        </>
                      ) : subscription.status === 'canceled' ? (
                        <>
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Cancelled
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {subscription.status}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {subscription.status === 'canceled' && (
                    <div className="mt-4 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-orange-900 dark:text-orange-300">
                            Subscription Cancelled
                          </h3>
                          <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                            Your subscription has been cancelled but you'll continue to have access to all professional features until{' '}
                            <span className="font-medium">{formatDate(subscription.nextBillingDate)}</span>. 
                            After this date, your account will be downgraded to the Free plan.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-4">
                      {subscription.nextBillingDate && (
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            subscription.status === 'canceled' 
                              ? 'bg-orange-50 dark:bg-orange-900/20' 
                              : 'bg-blue-50 dark:bg-blue-900/20'
                          }`}>
                            <Calendar className={`w-5 h-5 ${
                              subscription.status === 'canceled' 
                                ? 'text-orange-600 dark:text-orange-400' 
                                : 'text-blue-600 dark:text-blue-400'
                            }`} />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {subscription.status === 'canceled' ? 'Access expires' : 'Next billing date'}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {formatDate(subscription.nextBillingDate)}
                              {subscription.status === 'canceled' && (
                                <span className="block text-xs text-orange-600 dark:text-orange-400 mt-1">
                                  No automatic renewal
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {subscription.paymentMethod && (
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Payment method</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              •••• {subscription.paymentMethod.last4} (expires {subscription.paymentMethod.expiryMonth}/{subscription.paymentMethod.expiryYear})
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {subscription.plan !== 'free' && (
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Billing amount</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">{subscription.billing}</span>
                              {subscription.plan === 'pro' && subscription.nextBillingDate && subscription.tier !== 'Monthly' && (
                                <span className="text-gray-500 dark:text-gray-400">
                                  {' - '}Next renewal: {new Date(subscription.nextBillingDate).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Plan Features:</h4>
                      <ul className="space-y-2">
                        {subscription.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-4">
                    {subscription.plan !== 'free' && (
                      <button
                        onClick={openCustomerPortal}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage License & Billing
                      </button>
                    )}
                    
                    {subscription.plan === 'free' && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Purchase a Professional license below to access subscription management
                      </div>
                    )}
                    
                    <Link 
                      to="/account/billing-history"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Billing History
                    </Link>
                    
                    <Link
                      to="/account/settings?tab=payment" 
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payment Methods
                    </Link>
                  </div>
                  
                  {/* Stripe Test Mode Banner */}
                  <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-400">
                      <strong>Test Mode:</strong> Use card number 4242 4242 4242 4242 with any CVC and future expiry date.
                    </p>
                    {subscription.plan === 'free' && (
                      <button
                        onClick={testCheckout}
                        className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                      >
                        Test Checkout
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Activation Key Section - IMPORTANT */}
              {activationKey && (
                <div className="mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-6 rounded-lg border-2 border-amber-300 dark:border-amber-700 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-800/50 flex items-center justify-center flex-shrink-0">
                      <Key className="w-6 h-6 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200 mb-1">
                        Your Activation Key
                      </h3>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                        Use this key to activate Torque Professional software after downloading
                      </p>
                      
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 max-w-md">
                          <div className="relative">
                            <input
                              type={showActivationKey ? "text" : "password"}
                              value={activationKey}
                              readOnly
                              className="w-full px-4 py-3 pr-24 bg-white dark:bg-gray-800 border-2 border-amber-200 dark:border-amber-600 rounded-lg font-mono text-lg text-gray-900 dark:text-white shadow-sm"
                            />
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                              <button
                                onClick={() => setShowActivationKey(!showActivationKey)}
                                className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-md transition-colors"
                                title={showActivationKey ? "Hide key" : "Show key"}
                              >
                                {showActivationKey ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(activationKey);
                                  setKeyCopied(true);
                                  setTimeout(() => setKeyCopied(false), 2000);
                                }}
                                className="p-2 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-md transition-colors"
                                title="Copy to clipboard"
                              >
                                {keycopied ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <Copy className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center text-sm text-amber-600 dark:text-amber-400">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        <span>Keep this key secure. You'll need it to activate your software.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
              
              {/* Free Trial Banner with Activation Key */}
              {trialKey && (
                <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-6 rounded-lg border-2 border-green-300 dark:border-green-700 shadow-lg">
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800/50 flex items-center justify-center flex-shrink-0">
                    <Key className="w-6 h-6 text-green-700 dark:text-green-300" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-1">
                      Your 7-Day Free Trial Activation Key
                    </h3>
                    <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                      Your free trial was automatically activated when you created your account. Use this key to activate the trial version of Torque Professional.
                    </p>
                    
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 max-w-md">
                        <div className="relative">
                          <input
                            type={showTrialKey ? "text" : "password"}
                            value={trialKey}
                            readOnly
                            className="w-full px-4 py-3 pr-24 bg-white dark:bg-gray-800 border-2 border-green-200 dark:border-green-600 rounded-lg font-mono text-lg text-gray-900 dark:text-white shadow-sm"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                            <button
                              onClick={() => setShowTrialKey(!showTrialKey)}
                              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-md transition-colors"
                              title={showTrialKey ? "Hide key" : "Show key"}
                            >
                              {showTrialKey ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(trialKey);
                                setTrialKeyCopied(true);
                                setTimeout(() => setTrialKeyCopied(false), 2000);
                              }}
                              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-md transition-colors"
                              title="Copy to clipboard"
                            >
                              {trialKeyCopied ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <Copy className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center text-sm text-green-600 dark:text-green-400">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      <span>This trial key expires 7 days from account creation. Full access to all premium features included.</span>
                    </div>
                  </div>
                </div>
              </div>
              )}
              
              {/* Available plans */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition mb-8">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Available Plans</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Choose the plan that works best for you and your team</p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {plans.map((plan) => (
                      <div 
                        key={plan.id} 
                        className={`rounded-lg overflow-hidden ${plan.id === 'cloud' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-700 dark:to-indigo-800' : 'border'} transition-all ${
                          plan.current 
                            ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500 dark:ring-blue-400' 
                            : plan.id === 'cloud' 
                              ? '' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      >
                        <div className="p-6">
                          <div className="flex items-center mb-4">
                            <PlanIcon plan={plan} />
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
                                {plan.id === 'pro' && licenseStatus.hasPaidLicense && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Active License
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{plan.description}</p>
                          
                          {plan.price === 'Coming Soon' ? (
                            <div className="mb-6">
                              <div className="bg-blue-500/20 backdrop-blur-sm rounded-lg p-4">
                                <div className="text-center">
                                  <p className="text-xl font-bold text-white">Pricing will be announced soon</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="mb-6">
                              {plan.priceOptions ? (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Select Subscription Length:</h4>
                                  <div className="space-y-2">
                                    {plan.priceOptions.map((option, index) => {
                                      const tierKey = option.duration === 'Monthly' ? 'monthly' : 
                                                     option.duration === '3 Months' ? 'quarterly' :
                                                     option.duration === '6 Months' ? 'semiannual' : 'annual';
                                      return (
                                        <div 
                                          key={index} 
                                          onClick={() => !licenseStatus.hasPaidLicense && setSelectedTier(tierKey)}
                                          className={`${licenseStatus.hasPaidLicense ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} rounded-lg p-3 flex justify-between border-2 transition-all ${
                                            selectedTier === tierKey 
                                              ? 'bg-blue-50 dark:bg-blue-900/50 border-blue-500 dark:border-blue-400' 
                                              : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                          }`}
                                        >
                                          <div className="flex items-center">
                                            <div className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center ${
                                              selectedTier === tierKey 
                                                ? 'bg-blue-500' 
                                                : 'bg-gray-300 dark:bg-gray-600'
                                            }`}>
                                              {selectedTier === tierKey && (
                                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 10">
                                                  <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z"/>
                                                </svg>
                                              )}
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{option.duration}</span>
                                          </div>
                                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                                            {option.price} {option.savings && <span className="text-green-500">{option.savings}</span>}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div className="mt-3 text-center">
                                    <div className="text-lg font-bold text-gray-900 dark:text-white">Save up to 31%</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-300">with annual subscription</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-baseline">
                                  <span className="text-2xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                                  {plan.frequency && (
                                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                                      {plan.frequency}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <ul className="mt-4 space-y-2 mb-6">
                            {plan.features.map((feature, index) => (
                              <li key={index} className="flex items-start text-sm">
                                <CheckCircle className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <div className="mt-6">
                            {plan.id === 'pro' && licenseStatus.hasPaidLicense ? (
                              <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                                You have an active paid license. Use the button below to view, cancel, or change your subscription.
                              </div>
                            ) : !isVerified && plan.id === 'pro' ? (
                              <div className="mb-3 text-sm text-yellow-600 dark:text-yellow-400 flex items-center">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                Please verify your email before purchasing
                              </div>
                            ) : (
                              <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                                Currently selected: <strong>{getTierName(selectedTier)}</strong>
                              </div>
                            )}
                            <button
                              onClick={() => plan.id === 'pro' && licenseStatus.hasPaidLicense ? openCustomerPortal() : handleUpgrade(plan.id, plan.isCloud)}
                              disabled={plan.disabled || (plan.id === 'pro' && !isVerified && !licenseStatus.hasPaidLicense)}
                              className={`w-full inline-flex justify-center items-center px-4 py-2.5 border ${
                                plan.id === 'free_trial' ? 'border-transparent' : 'border-transparent'
                              } rounded-md shadow-sm text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                plan.id === 'cloud' ? 'focus:ring-indigo-500' : 'focus:ring-blue-500'
                              } ${
                                (plan.disabled || (plan.id === 'pro' && !isVerified && !licenseStatus.hasPaidLicense))
                                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-70'
                                  : plan.current
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : plan.id === 'free_trial'
                                      ? 'bg-green-600 text-white hover:bg-green-700'
                                      : plan.id === 'pro'
                                        ? licenseStatus.hasPaidLicense
                                          ? 'bg-green-600 text-white hover:bg-green-700'
                                          : 'bg-blue-600 text-white hover:bg-blue-700'
                                        : plan.id === 'cloud' && hasNotified
                                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                              } text-white`}
                            >
                              {plan.id === 'pro' && !plan.current ? (
                                !isVerified && !licenseStatus.hasPaidLicense ? (
                                  <>
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Verify Email to Purchase
                                  </>
                                ) : licenseStatus.hasPaidLicense ? (
                                  <>
                                    <Settings className="w-4 h-4 mr-2" />
                                    Manage Your License
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Purchase {getTierName(selectedTier)} Subscription
                                  </>
                                )
                              ) : plan.id === 'cloud' ? (
                                hasNotified ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Thank You!
                                  </>
                                ) : (
                                  <>
                                    <Bell className="w-4 h-4 mr-2" />
                                    Notify Me
                                  </>
                                )
                              ) : (
                                plan.cta
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* FAQ Section */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Frequently Asked Questions</h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">How do I change my plan?</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Click on "Choose Plan" to view available subscription options and select the one that best fits your needs. 
                          You can manage your subscription through the Stripe customer portal.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                          <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">What happens when I cancel?</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          When you cancel your subscription through the customer portal, you'll still have access to your paid features until the end of your current billing period.
                          After that, your account will be downgraded to the Free plan.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                          <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">How do I update my payment information?</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Use the "Manage Subscription" button to access the Stripe customer portal where you can update your payment method, download invoices, and manage your subscription.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                          <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">Is my payment information secure?</h3>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Yes, all payment processing is handled by Stripe, a PCI-compliant payment processor. Your credit card details are never stored on our servers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Torque Cloud</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Torque Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* New License Modal - shows only the newly purchased license */}
      <LicenseModal
        isOpen={showNewLicenseModal}
        onClose={() => setShowNewLicenseModal(false)}
        licenseKey={newPurchasedLicense?.licenseKey || ''}
        licenseType="paid"
        expirationDate={newPurchasedLicense?.expirationDate}
      />
      
      <Footer />
    </div>
  );
};

export default SubscriptionPage;